import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHtmlDataTheme } from '../../services/useHtmlDataTheme';
import { findScreenshotFile, loadScreenshot, type ScreenshotAsset } from '../screenshots';
import { ScreenshotPlaceholder } from './ScreenshotPlaceholder';

/** Used until the manifest gives the real proportions (most captures are wider than tall). */
const FALLBACK_RATIO = '16 / 10';

/**
 * Shows the screenshot variant matching the UI language and theme. The space is
 * reserved at the image's real proportions and a skeleton shows until it has
 * loaded, so the guide doesn't jump; images below the fold load lazily. Falls
 * back to the placeholder when the key has no image or it fails to load.
 */
export const HelpScreenshot: React.FC<{ screenshotKey: string; caption: string }> = ({
  screenshotKey,
  caption,
}) => {
  const { i18n } = useTranslation();
  const theme = useHtmlDataTheme() as 'light' | 'dark';
  const file = findScreenshotFile(screenshotKey, { language: i18n.language, theme });

  const [asset, setAsset] = useState<ScreenshotAsset | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!file) return undefined;
    let active = true;
    setAsset(null);
    setLoaded(false);
    setFailed(false);
    loadScreenshot(file)
      .then((a) => active && setAsset(a))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [file]);

  if (!file || failed) return <ScreenshotPlaceholder caption={caption} screenshotKey={screenshotKey} />;

  const ratio = asset?.width && asset?.height ? `${asset.width} / ${asset.height}` : FALLBACK_RATIO;
  return (
    <figure className="my-5">
      <div
        className="relative w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm dark:border-gray-700"
        style={{ aspectRatio: ratio }}
      >
        {!loaded && (
          <div
            data-testid="help-screenshot-skeleton"
            aria-hidden="true"
            className="absolute inset-0 animate-pulse bg-gray-100 motion-reduce:animate-none dark:bg-gray-800"
          />
        )}
        {asset && (
          <img
            src={asset.url}
            alt={caption}
            width={asset.width}
            height={asset.height}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 motion-reduce:transition-none ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">{caption}</figcaption>
      )}
    </figure>
  );
};
